import React, { useEffect, ChangeEvent } from 'react';

import { TextButton, Input } from '../../shared/ui';

import { BonusInfoLabel } from './bonus-info-label';

interface IBonusInputProps {
  min?: number;
  max?: number;
  maxPercent?: number;
  value: number | null;
  onChanged?: (val: number | null) => void;
  disabled?: boolean;
}

const BonusInput: React.FC<IBonusInputProps> = ({
  min = 0,
  max = 0,
  maxPercent = 0,
  onChanged,
  value,
  disabled = false,
}) => {
  // Ensure value stays within bounds and is valid on mount or prop change
  useEffect(() => {
    // Check if the value is a valid number and within bounds.
    // Use `null` to represent an empty or invalid state.
    if (value) {
      const numeric = Number.parseFloat(`${value}`);
      const isInvalidValue = Number.isNaN(numeric);

      if (isInvalidValue) {
        onChanged?.(min);
      } else if (numeric > max) {
        onChanged?.(max);
      } else {
        if (numeric < min) {
          onChanged?.(min);
        }
      }
    }
  }, [min, max, value, onChanged]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    const typed = e.target.value;
    if (typed === '') return onChanged?.(null);
    const numeric = parseFloat(typed);
    // Not a number: no state change, so the controlled input keeps its last value.
    if (!Number.isNaN(numeric)) onChanged?.(numeric);
  };

  const handleBlur = () => {
    if (disabled) return;
    if (value === null || value < min) onChanged?.(min);
    else if (value > max) onChanged?.(max);
  };

  return (
    <div className="flex w-full flex-col gap-1">
      <Input
        size="lg"
        inputMode="decimal"
        aria-label="Сколько бонусов списать"
        value={value || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        placeholder={max > 0 ? `бонусами от ${min} до ${max}` : undefined}
        icon={
          disabled
            ? null
            : {
                type: 'suffix',
                icon: (
                  <TextButton size="sm" tone="primary" onClick={() => onChanged?.(max)}>
                    Макс
                  </TextButton>
                ),
              }
        }
      />
      <BonusInfoLabel limit={maxPercent} classes={'text-accent !text-ui-s-strong'} />
    </div>
  );
};

export default BonusInput;
